/**
 * Fetches store reviews from the Judge.me carousel endpoint and renders them
 * into the theme's own card markup. The aggregate rating card next to the
 * slider is rendered server side from shop metafields and is never touched
 * here, so the section still shows something if this request fails.
 */
class JudgemeReviews extends HTMLElement {
  connectedCallback() {
    this.slider = this.querySelector('[data-jm-slider]');
    this.template = this.querySelector('[data-jm-card-template]');
    if (!this.slider || !this.template) return;

    this.locale = this.dataset.locale || document.documentElement.lang || 'de';
    // `routes.root_url` is `/` on the primary locale and `/fr`, `/en`, ... on
    // the others, while the feed always hands back an unprefixed path. Strip
    // the trailing slash so the two concatenate to a single one.
    this.rootUrl = (this.dataset.rootUrl || '/').replace(/\/$/, '');
    // Off by section setting, the badge follows Judge.me's own verified flag.
    this.alwaysVerified = this.dataset.alwaysVerified === 'true';

    this.setUpArrows();
    this.load();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  setUpArrows() {
    this.prev = this.querySelector('[data-jm-prev]');
    this.next = this.querySelector('[data-jm-next]');
    if (!this.prev && !this.next) return;

    this.prev?.addEventListener('click', () => this.step(-1));
    this.next?.addEventListener('click', () => this.step(1));

    this.slider.addEventListener('scroll', () => {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => this.updateArrows());
    });

    this.resizeObserver = new ResizeObserver(() => this.updateArrows());
    this.resizeObserver.observe(this.slider);

    this.updateArrows();
  }

  /** Width of one card plus the gap between cards. */
  get stepSize() {
    const slide = this.slider.querySelector('.jm-reviews__slide');
    if (!slide) return this.slider.clientWidth;

    const gap = parseFloat(getComputedStyle(this.slider).columnGap) || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  step(direction) {
    this.slider.scrollBy({ left: direction * this.stepSize, behavior: 'smooth' });
  }

  updateArrows() {
    if (!this.prev && !this.next) return;

    const { scrollLeft, scrollWidth, clientWidth } = this.slider;
    // Sub-pixel layout means the far end rarely lands on an exact integer.
    const maxScroll = scrollWidth - clientWidth;
    const overflows = maxScroll > 1;

    [this.prev, this.next].forEach((arrow) => {
      if (arrow) arrow.hidden = !overflows;
    });

    if (!overflows) return;

    const focused = document.activeElement;

    if (this.prev) this.prev.disabled = scrollLeft <= 1;
    if (this.next) this.next.disabled = scrollLeft >= maxScroll - 1;

    // A disabled arrow is removed from the layout, so a keyboard user who just
    // reached the end would lose their place. Hand focus to the opposite arrow.
    if ((focused === this.prev || focused === this.next) && focused.disabled) {
      const sibling = focused === this.prev ? this.next : this.prev;
      (sibling && !sibling.disabled ? sibling : this.slider).focus();
    }
  }

  get endpoint() {
    const params = new URLSearchParams({
      reviews_selection: this.dataset.reviewsSelection || 'all',
      carousel_type: 'cards',
      star_rating: this.dataset.starRating || '4_to_5_star',
      max_reviews: this.dataset.maxReviews || '20',
      url: this.dataset.shopUrl || window.location.origin,
      shop_domain: this.dataset.shopDomain,
      platform: 'shopify',
      primary_language: this.dataset.primaryLanguage || 'de',
      translation_locale: this.locale,
    });

    return `https://cdn.judge.me/reviews/reviews_for_carousel?${params}`;
  }

  async load() {
    let reviews;

    try {
      const response = await fetch(this.endpoint);
      if (!response.ok) throw new Error(`Judge.me responded with ${response.status}`);
      ({ reviews } = await response.json());
    } catch (error) {
      console.warn('[judgeme-reviews] could not load reviews', error);
      return;
    }

    // An invalid filter value returns 200 with an empty array rather than an
    // error, so treat "no reviews" the same as a failed request and keep
    // whatever the section already rendered.
    if (!Array.isArray(reviews) || reviews.length === 0) return;

    this.render(reviews);
  }

  render(reviews) {
    const fragment = document.createDocumentFragment();

    this.newestFirst(reviews).forEach((review) => {
      const card = this.buildCard(review);
      if (card) fragment.appendChild(card);
    });

    if (!fragment.childElementCount) return;

    this.slider.replaceChildren(fragment);
    this.slider.scrollLeft = 0;
    this.updateArrows();
  }

  /**
   * Judge.me happens to hand back the feed newest first, but the carousel
   * endpoint takes no sort parameter and makes no promise about it, so order
   * it here. Reviews with no usable date sort to the end.
   */
  newestFirst(reviews) {
    const timestamp = (review) => {
      const parsed = Date.parse(review?.created_at);
      return Number.isNaN(parsed) ? 0 : parsed;
    };

    return [...reviews].sort((a, b) => timestamp(b) - timestamp(a));
  }

  buildCard(review) {
    const slide = this.template.content.firstElementChild.cloneNode(true);

    const stars = slide.querySelector('[data-jm-stars]');
    const date = slide.querySelector('[data-jm-date]');
    const body = slide.querySelector('[data-jm-body]');
    const author = slide.querySelector('[data-jm-author]');
    const verified = slide.querySelector('[data-jm-verified]');
    const link = slide.querySelector('[data-jm-link]');

    if (stars) {
      const rating = Math.max(0, Math.min(5, Math.round(Number(review.rating) || 0)));
      Array.from(stars.children).forEach((star, index) => {
        star.classList.toggle('jm-star--filled', index < rating);
      });
      const label = stars.dataset.jmStarsLabel || 'RATING / 5';
      stars.setAttribute('aria-label', label.replace('RATING', rating));
    }

    // Missing date and author lines are left empty rather than removed: the
    // card reserves a row for each so every card's stars, text and author
    // stay on the same baseline.
    if (date) {
      const relative = this.relativeDate(review.created_at);
      if (relative) {
        date.textContent = relative;
        date.setAttribute('datetime', review.created_at);
      }
    }

    // `body` is the plain-text field; `body_html` is deliberately unused so
    // review content can never inject markup into the page.
    if (body) body.textContent = review.body || '';

    if (author && review.reviewer_name) author.textContent = review.reviewer_name;

    if (verified && !this.alwaysVerified && !review.verified_buyer) verified.remove();

    if (link) this.applyProductLink(link, review, slide);

    return slide;
  }

  /**
   * Only product reviews carry a product page to link to; a review of the shop
   * itself comes back with `product_url: "/"`. The feed's own absolute field,
   * `product_url_with_utm`, is deliberately unused: it always points at the
   * shop's canonical domain, which would move the shopper off a theme preview
   * or a market domain. The URL is assembled the way Liquid assembles one
   * instead - `routes.root_url` (locale aware) plus the path - and resolved
   * against the origin the shopper is already on.
   */
  applyProductLink(link, review, slide) {
    const path = typeof review.product_url === 'string' ? review.product_url : '';

    if (!review.product_in_store || !path.startsWith('/products/')) {
      link.remove();
      return;
    }

    const title = (review.product_title || '').trim();
    const label = link.dataset.jmLinkLabel || '[product]';

    link.href = new URL(`${this.rootUrl}${path}`, window.location.origin).href;
    // The card's own text is hidden from the link, so it needs a name of its
    // own. Without a product title, drop the placeholder and keep the wording.
    link.setAttribute(
      'aria-label',
      label.replace('[product]', title).replace(/\s+/g, ' ').replace(/[:\s]+$/, '').trim()
    );
    link.hidden = false;

    slide.querySelector('.jm-review-card')?.classList.add('jm-review-card--linked');
  }

  relativeDate(value) {
    if (!value || typeof Intl === 'undefined' || !Intl.RelativeTimeFormat) return null;

    const timestamp = Date.parse(value);
    if (Number.isNaN(timestamp)) return null;

    const seconds = (timestamp - Date.now()) / 1000;
    const units = [
      ['year', 31536000],
      ['month', 2592000],
      ['week', 604800],
      ['day', 86400],
      ['hour', 3600],
      ['minute', 60],
    ];

    const formatter = new Intl.RelativeTimeFormat(this.locale, { numeric: 'always' });

    for (const [unit, secondsPerUnit] of units) {
      if (Math.abs(seconds) >= secondsPerUnit) {
        return formatter.format(Math.round(seconds / secondsPerUnit), unit);
      }
    }

    return formatter.format(Math.round(seconds), 'second');
  }
}

customElements.define('judgeme-reviews', JudgemeReviews);
